import {
  Baby,
  BookOpen,
  BookUser,
  Building2,
  Calendar,
  CalendarRange,
  FileText,
  Flame,
  Globe,
  KeyRound,
  Landmark,
  LayoutDashboard,
  Library,
  ListChecks,
  Mic2,
  Music2,
  Package,
  PenLine,
  Settings,
  ShoppingCart,
  Sparkles,
  UserCircle,
  Users,
  Users2,
  Warehouse,
} from 'lucide-react';

// Zijbalksecties, in weergavevolgorde. `label: null` betekent platformniveau --
// geen sectiekop/scheidingslijn (zie docs/navigation-architecture.md). Elk item
// behoudt exact de requiredModule/requiredCapability-combinatie die het al had
// vóór deze herstructurering; dit is een presentatie-herindeling, geen wijziging
// in autorisatielogica.
export const NAV_SECTIONS = [
  {
    id: 'platform',
    label: null,
    items: [
      { id: 'dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      {
        id: 'central-planning', label: 'Centrale Planning', href: '/planning', icon: CalendarRange,
        requiredModule: 'central_planning', requiredCapability: 'central_planning_access',
      },
    ],
  },
  {
    id: 'bookings',
    label: 'Reserveringen',
    items: [
      {
        id: 'bookings-overview', label: 'Overzicht', href: '/bookings/overview', icon: LayoutDashboard,
        requiredModule: 'reserveringen', requiredCapability: 'reservations.view',
      },
      {
        id: 'calendar', label: 'Kalender', href: '/bookings', icon: Calendar,
        requiredModule: 'reserveringen', requiredCapability: 'reservations.view',
      },
      {
        id: 'resources', label: 'Resources', href: '/resources', icon: Building2,
        requiredModule: 'reserveringen', requiredCapability: 'reservations.manage',
      },
      {
        id: 'public-page', label: 'Publieke boekingspagina', href: '/bookings/public', icon: Globe,
        requiredModule: 'reserveringen', requiredCapability: 'reservations.manage',
      },
    ],
  },
  {
    id: 'people',
    label: 'Mensen & groepen',
    items: [
      { id: 'relations', label: 'Relaties', href: '/relations', icon: UserCircle },
      { id: 'members', label: 'Ledenbestand', href: '/members', icon: BookUser },
      { id: 'connectgroepen', label: 'Connectgroepen', href: '/connectgroepen', icon: Users2 },
      { id: 'team', label: 'Team', href: '/team', icon: Users },
    ],
  },
  {
    id: 'ministries',
    label: 'Bedieningen',
    items: [
      {
        id: 'worship', label: 'Worship', href: '/worship', icon: Music2,
        requiredModule: 'worship',
      },
      {
        id: 'kinderwerk', label: 'Kinderwerk', href: '/kinderwerk', icon: Baby,
        requiredCapability: 'kinderwerk_access', requiredModule: 'kinderwerk',
      },
      {
        id: 'connectlessen', label: 'Connectlessen', href: '/connectlessen', icon: BookOpen,
        requiredCapability: 'connectlessen_manage',
      },
    ],
  },
  {
    id: 'facilities',
    label: 'Facilitair',
    items: [
      {
        id: 'keys', label: 'Sleutelbeheer', href: '/keys', icon: KeyRound,
        requiredCapability: 'keys_access', requiredModule: 'sleutelbeheer',
      },
      { id: 'inventory', label: 'Voorraadbeheer', href: '/inventory', icon: Package, requiredModule: 'voorraadbeheer' },
      { id: 'buildings', label: 'Gebouwenbeheer', href: '/buildings', icon: Warehouse, requiredModule: 'gebouwbeheer' },
      {
        id: 'heating', label: 'Heating', href: '/heating', icon: Flame,
        requiredCapability: 'heating_admin', requiredModule: 'heating',
      },
      {
        id: 'contracts', label: 'Contractbeheer', href: '/contracts', icon: FileText,
        requiredCapability: 'contracts_access', requiredModule: 'contractbeheer',
      },
    ],
  },
  {
    id: 'knowledge',
    label: 'Kennis & content',
    items: [
      {
        id: 'library', label: 'Bibliotheek', href: '/library', icon: Library,
        requiredCapability: 'library_access', requiredModule: 'bibliotheek',
      },
      {
        id: 'writer', label: 'Writer Studio', href: '/writer', icon: PenLine,
        requiredCapability: 'writer_access', requiredModule: 'writer_studio',
      },
    ],
  },
  {
    id: 'commerce',
    label: 'Commerce',
    items: [
      {
        id: 'commerce', label: 'Commerce', href: '/commerce', icon: ShoppingCart,
        requiredCapability: 'commerce_manage', requiredModule: 'commerce',
      },
    ],
  },
  {
    id: 'leiderschap',
    label: 'Leiderschap',
    items: [
      {
        id: 'leiderschap', label: 'Leiderschap', href: '/leiderschap', icon: Landmark,
        requiredCapability: 'leiderschap_access', requiredModule: 'leiderschap',
      },
    ],
  },
  {
    id: 'sprekers',
    label: 'Sprekers',
    items: [
      {
        id: 'sprekers', label: 'Sprekers', href: '/sprekers', icon: Mic2,
        requiredCapability: 'speakers_access', requiredModule: 'sprekers',
      },
    ],
  },
  {
    id: 'sites',
    label: 'Website',
    items: [
      { id: 'sites', label: 'Sites', href: '/sites', icon: Globe, requiredModule: 'sites' },
    ],
  },
  {
    id: 'admin',
    label: 'Beheer',
    items: [
      { id: 'usecases', label: 'Usecases', href: '/usecases', icon: ListChecks, requiredCapability: 'usecases_view' },
      { id: 'settings', label: 'Instellingen', href: '/settings', icon: Settings },
    ],
  },
];

export function isItemVisible(item, { hasCapability, hasModule }) {
  if (item.requiredModule && !hasModule(item.requiredModule)) return false;
  if (item.requiredCapability && !hasCapability(item.requiredCapability)) return false;
  return true;
}

export function getVisibleSections(sections, authFlags) {
  return sections
    .map((section) => ({ ...section, items: section.items.filter((item) => isItemVisible(item, authFlags)) }))
    .filter((section) => section.items.length > 0);
}

export function getDevelopmentSection({ hasCapability, developmentMode }) {
  if (!developmentMode) return null;
  const canFlow = hasCapability('flow_view') || hasCapability('flow_manage');
  const canUsecases = hasCapability('usecases_view');
  const items = [
    canUsecases && { id: 'development-usecases', label: 'Usecases', href: '/usecases', icon: ListChecks },
    canFlow && { id: 'flow-studio', label: 'Flow Studio', href: '/flows', icon: Sparkles },
  ].filter(Boolean);
  return items.length ? { id: 'development', label: 'Ontwikkeling', items } : null;
}
